// medplum-writes.js — ONLINE-PRIMARY consult WRITE path (draft save + sign).
// The browser writes DIRECTLY to Medplum as the logged-in doctor (RLS-scoped by
// their own token via the shared getMedplum() session — see medplum-client.js),
// mirroring the read path (medplum-encounter.js) and the queue/reception/… read
// loaders. This replaces the two state-defining Express writes ConsultCompact.jsx
// makes — the autosave PATCH /consult (saveDraft) and the POST /prescription/sign
// (sign) — with a FHIR-direct write and a Bot invocation respectively. The screen
// JSX is unchanged (the mockups ARE the design — AGENTS.md §7). This path is
// Medplum-ONLY and FAIL LOUD (the Express write fallbacks were deleted
// 2026-07-02): a failed save/sign surfaces as a visible error in the consult,
// never a silent write to a different store.
//
// Two writes, two mechanisms, matching what already exists in Medplum:
//   • saveDraftToMedplum → the consult-draft Composition IS the draft's FHIR home
//     (a preliminary Composition, type coding.code='consult-draft', carrying the
//     draft JSON in a single OPD_DRAFT extension.valueString — exactly what the
//     read path's shapeConsultDraft/draftJsonFromComposition reads back, and what
//     the seed writes). Update-in-place with If-Match optimistic concurrency when
//     the draft Composition already exists; conditional-create (If-None-Exist on a
//     stable per-encounter identifier) on first save, because a browser client
//     can't mint a server-authoritative UUID and two boxes editing the same
//     encounter must not create two draft Compositions.
//   • signViaMedplum → the proven `sign` Bot (scripts/medplum/seed-demo-queue.mjs,
//     _spike-sign-bot.mjs): one atomic transaction Bundle (allergy safety-check →
//     MedicationRequests + Condition + visit-summary Composition + Provenance +
//     Encounter→finished), idempotent by the Composition.identifier business key.
//     It runs runAsUser:true, so the sign writes are still subject to THIS
//     doctor's AccessPolicy — no superuser bypass. We only call the Bot; it owns
//     the transaction.
import { getMedplum } from './medplum-client.js';
import { ACCESS_INDEX_SETTLE_MS, grantChartAccess } from './medplum-access.js';
import { getOutbox } from './outbox.js';
import { updateEntry, draftCreateEntry } from './outbox-ops.js';
import { OPD_QUEUE_CLASS_EXT, QUEUE_CLASS, readQueueClass, buildQueueClassExtension } from './queue-engine.js';

// ── Offline write outbox integration (ONLY the two UPDATE writes) ─────────────
// saveDraftToMedplum (update-in-place path) and updatePatientInMedplum enqueue
// an op:'update' entry and drain. ONE write path online and offline
// (REDESIGN_ONLINE_PRIMARY.md): online, enqueue() drains synchronously, so the
// caller gets the landed result AND a 412 still surfaces as the typed
// DraftConflictError / PatientConflictError exactly as before (we inspect the
// drained entry's parked status and re-throw). Offline, the entry sits 'queued'
// and replays on reconnect; a 412 discovered on that replay path parks the row
// 'conflict' for a human — the offline analogue of the typed error.
//
// NOT queued, ever — these FAIL LOUD offline (a queued sign/payment/AI/grant
// would be a silent clinical or money write the clinician thinks succeeded):
//   • signViaBot         — the sign Bot transaction (below) is online-only.
//   • payments           — money writes must confirm synchronously.
//   • AI / assistant      — online-only by the redesign's three gates.
//   • access grants       — a security decision must not replay unattended.
//   • anonymous kiosk writes — never widen an anonymous surface via a queue.
// (Assertion of this boundary is by construction: only the two update helpers
// below import the outbox; nothing else in this module does.)
//
// Replays an op:'update' entry through the outbox and, on the synchronous online
// drain, maps a parked 'conflict' back onto the caller-supplied typed error so
// the screen's existing conflict UX (draft banner / demographics diff) keeps
// working. `makeConflict(current)` builds the right typed error; `readCurrent`
// (optional) fetches the winner's copy for the diff panel.
// Harness seam (mirrors medplum-patient-create.outboxForCreate): a Node/vitest
// test injects an in-memory-backed Outbox via globalThis.__HEALTHER_HARNESS_OUTBOX__
// so it never touches IndexedDB. The app always gets the real singleton.
async function resolveOutbox() {
  if (typeof globalThis !== 'undefined' && globalThis.__HEALTHER_HARNESS_OUTBOX__) {
    return globalThis.__HEALTHER_HARNESS_OUTBOX__;
  }
  return getOutbox();
}

async function enqueueUpdate({ resourceType, body, targetId, ifMatch, profileId }, makeConflict, readCurrent) {
  // INVARIANT (O4): a queued update MUST carry an ifMatch (the versionId read
  // at load time). Without it the replay PUT goes out with no If-Match header
  // (outbox.js:215 falsy branch) — an UNCONDITIONAL overwrite that silently
  // clobbers a newer server copy (blind last-write-wins). A clinical update
  // with no optimistic-concurrency version is a hard programming error, so fail
  // loud here rather than queue a write that can lose a colleague's change.
  // This makes outbox.js:215's headerless branch unreachable for enqueued
  // updates (it stays as defense-in-depth; the guarantee comes from here).
  if (ifMatch === null || ifMatch === undefined) {
    throw new Error('enqueueUpdate: refusing to queue an update with no ifMatch versionId — a blind overwrite must never be queued (O4)');
  }
  const outbox = await resolveOutbox();
  const entry = updateEntry({ resourceType, body, targetId, ifMatch, profileId });
  const landed = await outbox.enqueue(entry);
  if (landed && landed.status === 'conflict') {
    // Online drain hit a 412 — surface it the way the pre-outbox path did.
    let current = null;
    if (readCurrent) { try { current = await readCurrent(); } catch (_) { /* best-effort */ } }
    throw makeConflict(current);
  }
  return landed;
}

// Same custom-extension / identifier conventions the read shaper and the seed use
// — kept in lock-step so a saved draft round-trips through the read path unchanged.
const OPD_DRAFT = 'https://healther.io/opd/draft';        // Composition.extension valueString = draft JSON
const OPD_TOKEN = 'https://healther.io/opd/token';        // Encounter identifier system (slug ↔ UUID)
const DRAFT_KEY = 'https://healther.io/opd/draft-key';    // stable per-encounter conditional-create key
const CONSULT_DRAFT_CODE = 'consult-draft';

// A 412 (version mismatch) on the draft PUT means another box/tab signed or saved
// a newer draft under us. We THROW this typed error rather than silently
// re-reading and retrying: a clinician editing a prescription MUST see that their
// buffer is stale (they may be about to overwrite a colleague's change), and the
// screen decides how to surface it. Fail loud — no speculative retry (AGENTS §1.3).
export class DraftConflictError extends Error {
  constructor(message) { super(message || 'draft changed on the server since it was loaded'); this.name = 'DraftConflictError'; this.conflict = true; this.status = 412; }
}

// (412/precondition classification now lives in the outbox engine — the two
// update writers below route through it and map the parked 'conflict' back onto
// the typed DraftConflictError / PatientConflictError; see enqueueUpdate above.)

// Builds the consult-draft Composition body for `encounterId`, carrying `draft`
// (the screen's draftBody() shape) as OPD_DRAFT extension JSON. Mirrors the seed's
// draft Composition exactly (status:preliminary, type coding.code='consult-draft')
// so the read path picks it up. `patientRef`/`authorRef` are stamped when known so
// the row-level policy's %profile / compartment criteria match; both are optional.
function buildDraftComposition({ encounterId, draft, patientRef, authorRef, id }) {
  const comp = {
    resourceType: 'Composition',
    status: 'preliminary',
    type: { coding: [{ code: CONSULT_DRAFT_CODE }], text: CONSULT_DRAFT_CODE },
    // Stable per-encounter identifier so the FIRST save is a conditional-create
    // (If-None-Exist on this value) — one draft Composition per encounter, no
    // duplicate-on-double-save race, no client-minted UUID needed. FHIR R4
    // Composition.identifier is 0..1 (a single Identifier, not an array) — matches
    // the sign Bot's visit-summary Composition, which the seed writes the same way.
    identifier: { system: DRAFT_KEY, value: encounterId },
    encounter: { reference: 'Encounter/' + encounterId },
    date: new Date().toISOString(),
    title: 'Consult draft',
    extension: [{ url: OPD_DRAFT, valueString: JSON.stringify(draft || {}) }],
    section: [{ title: 'Plan', text: { status: 'generated', div: '<div xmlns="http://www.w3.org/1999/xhtml">draft</div>' } }],
  };
  if (id) comp.id = id;
  if (patientRef) comp.subject = { reference: patientRef };
  if (authorRef) comp.author = [{ reference: authorRef }];
  return comp;
}

// Persists the consult draft to Medplum (the saveDraft PATCH /consult replacement).
//   { encounterId, draft, compositionId, versionId } — compositionId/versionId come
//   from the read path's encounter.draftRef.{id,versionId}. When compositionId is
//   present we PUT with If-Match: W/"<versionId>" (optimistic concurrency, the CAS
//   pattern from _spike-counter-bot); a 412 throws DraftConflictError. When there's
//   no compositionId yet (draftRef.id === null → no draft Composition exists), we
//   conditional-create on the DRAFT_KEY identifier so a concurrent first-save can't
//   spawn a second draft.
// Returns { id, versionId, status } of the saved draft Composition, so the caller can
// refresh its in-memory draftRef for the next save (avoids a stale-version 412 on
// the very next keystroke autosave).
export async function saveDraftToMedplum({ encounterId, draft, compositionId, versionId }) {
  if (!encounterId) throw new Error('saveDraftToMedplum: missing encounterId');
  const { medplum } = await getMedplum();

  // Best-effort authoring refs so the row-level policy's %profile/compartment
  // criteria match. The doctor's own profile is the author; the patient ref comes
  // off the encounter (a cheap read the client already did for the chart, but we
  // don't have it here — resolve from the profile + encounter only when creating).
  // Capture the authoring profile via the ASYNC form (O5). The SYNC
  // medplum.getProfile() returns undefined right after a session resume (it
  // races the fire-and-forget /auth/me fetch — see medplum-client.js
  // getMedplum). A null profile here would queue the update with
  // profileId:null, and the different-profile guard in the engine
  // (outbox.js:167 — `e.profileId && liveProfileId && …`) is SKIPPED when
  // profileId is falsy, so the write could replay under whoever logs in next
  // (§2 wrong-user hole). A clinical write with no known author must not queue:
  // fail loud so profileId is ALWAYS present and the guard ALWAYS fires.
  const profile = await medplum.getProfileAsync();
  if (!profile) throw new Error('saveDraftToMedplum: no logged-in profile — refusing to queue a clinical write with no known author (O5)');
  const authorRef = profile.resourceType + '/' + profile.id;

  if (compositionId) {
    // UPDATE-IN-PLACE with optimistic concurrency, through the offline outbox.
    // If-Match guards against a concurrent save/sign having advanced the version
    // under us. Online, enqueue() drains synchronously → a 412 surfaces as the
    // typed DraftConflictError below (unchanged UX); offline, the save queues
    // and replays on reconnect (a 412 on that path parks the row 'conflict').
    const body = buildDraftComposition({ encounterId, draft, authorRef, id: compositionId });
    const landed = await enqueueUpdate(
      { resourceType: 'Composition', body, targetId: compositionId, ifMatch: versionId, profileId: profile.id },
      () => new DraftConflictError(),
    );
    // The optimistic online path lands with a real saved resource; return the
    // fresh id/versionId so the caller refreshes draftRef for the next autosave.
    // Prefer the server versionId the drain captured on the terminal snapshot (no
    // extra round-trip, and the deterministic fresh version — m-29); the 'done'
    // row was GC'd from the queue, so re-read only if the drain didn't carry one.
    if (landed && landed.status === 'done') {
      if (landed.resolvedVersionId) {
        return { id: landed.resolvedId || compositionId, versionId: landed.resolvedVersionId, status: 'preliminary' };
      }
      const saved = await medplum.readResource('Composition', landed.resolvedId || compositionId);
      return { id: saved.id, versionId: saved.meta && saved.meta.versionId, status: saved.status };
    }
    if (landed && landed.status === 'failed') {
      throw new Error(landed.lastError || 'consult draft save was rejected by Medplum');
    }
    if (landed && landed.status === 'queued') {
      // Genuinely OFFLINE: the update sits 'queued' and replays on reconnect.
      // Return the known id/version and make that state explicit. Save may remain
      // available offline, but signing is online-only and must never claim it
      // signed bytes that have not reached Medplum yet.
      return { id: compositionId, versionId, queued: true };
    }
    // landed === undefined → the update LANDED on the eager online drain and was
    // then garbage-collected out of the queue in the SAME drain: a 'done'
    // op:'update' row carries no localRef, so _gcDone() removes it and enqueue()'s
    // store.get(id) returns undefined (outbox.js _drainOnce → _gcDone → enqueue
    // return). This is a SUCCESS, not a queued write. The CREATE path already
    // treats "GC'd → undefined" as landed (below); the UPDATE path must match —
    // otherwise EVERY online re-save of an existing draft returns a false
    // { queued: true }, and ConsultCompact.sign()'s `savedDraft.saved?.queued`
    // preflight bails "Sign needs a live connection" on any consult with a saved
    // draft (the deterministic sign-blocked bug). Re-resolve by the stable
    // DRAFT_KEY so the caller refreshes draftRef to the FRESH id/version — this
    // also closes the stale-version-on-next-sign window: returning the pre-save
    // versionId here would let a later sign submit an outdated draft version that
    // the sign transaction rejects, with no recovery but a reload.
    const ref = await resolveSavedDraftRef(encounterId);
    if (ref) return { id: ref.id, versionId: ref.versionId, status: ref.status };
    // Landed but unresolvable (unexpected) — degrade to a queued signal rather than
    // hand back a stale version a later sign would submit and have rejected.
    return { id: compositionId, versionId, queued: true };
  }

  // FIRST save — no draft Composition exists yet. Route the conditional-create
  // through the offline outbox (mirroring the update path above) so a first save
  // made OFFLINE queues and replays on reconnect instead of throwing and
  // PERMANENTLY blocking every save this session (the bug this closes). The entry
  // is a conditional-create keyed on the stable DRAFT_KEY identifier, so replay is
  // idempotent: a double-replay, or two boxes racing the first save, converge on
  // ONE draft Composition — never a twin.
  const body = buildDraftComposition({ encounterId, draft, authorRef });
  const outbox = await resolveOutbox();

  // CAUSAL ORDER / no-twin coalescing: while OFFLINE the caller keeps autosaving
  // and (because we return no server id below) each call lands back here. Rather
  // than pile up a second create — whose body createResourceIfNoneExist would drop
  // on replay — we replace the still-queued create's body with the newest draft.
  // Every save carries the FULL draft, so the latest queued body IS the whole
  // current draft, and it lands as ONE Composition on the first reconnect.
  const pending = await outbox.findPendingCreate(DRAFT_KEY, encounterId);
  const landed = pending
    ? await outbox.coalesceCreateBody(pending.id, body)
    : await outbox.enqueue(draftCreateEntry({ body, slug: encounterId, slugSystem: DRAFT_KEY, profileId: profile.id }));

  // A parked row surfaces its terminal status; a row that LANDED on the eager
  // online drain has already been garbage-collected out of the queue (the engine
  // deletes a 'done' create with no pending child), so `landed` is then undefined.
  if (landed && landed.status === 'failed') {
    throw new Error(landed.lastError || 'consult draft save was rejected by Medplum');
  }
  if (landed && landed.status === 'conflict') {
    // A 412 on the create's replay (the draft was created under us) — surface it
    // the same way the update path does so the screen's conflict UX fires.
    throw new DraftConflictError();
  }
  if (landed && landed.status === 'queued') {
    // Offline: no server id yet. Return a queued signal WITHOUT a fabricated id —
    // the caller shows "waiting to sync", and its next full-draft save coalesces
    // into this same queued create (above) until it replays on reconnect.
    return { queued: true };
  }
  // Landed (status 'done', or GC'd → undefined). Resolve the saved draft by its
  // stable DRAFT_KEY so the caller gets the real id/version and its NEXT save
  // takes the update-in-place path.
  const ref = await resolveSavedDraftRef(encounterId);
  if (ref) return { id: ref.id, versionId: ref.versionId, status: ref.status };
  // A landed-but-unresolvable draft is unexpected; degrade to a queued signal
  // rather than fabricate an id (the next full-draft save reconciles).
  return { queued: true };
}

/**
 * Resolve the encounter's saved consult-draft Composition to the exact
 * { id, versionId, status } a sign-by-reference needs — reading AS THE CALLER
 * (RLS scopes it). Callers that hold no live draftRef (the paper-Rx flow, the
 * generic doctor:sign click-router) use this instead of sending draft content:
 * the sign Bot only signs the SAVED draft, never browser-supplied clinical
 * bytes. Returns null when no draft Composition exists for the encounter.
 * `status === 'final'` means the visit is already signed (the sign transaction
 * froze the draft) — callers must treat that as already-signed, NEVER save over
 * it (a save would flip the frozen record back to preliminary).
 */
export async function resolveSavedDraftRef(encounterId) {
  if (!encounterId) throw new Error('resolveSavedDraftRef: missing encounterId');
  const { medplum } = await getMedplum();
  const comp = await medplum.searchOne(
    'Composition',
    'identifier=' + DRAFT_KEY + '|' + encodeURIComponent(encounterId),
  );
  if (!comp) return null;
  // medCount: how many medications the SAVED draft holds — lets a caller that
  // does not itself hold the draft object (the bridge-core doctor:sign router)
  // still arm signViaBot's stale-Bot tripwire. Parsed from the same OPD_DRAFT
  // extension the sign Bot reads; a malformed/absent blob yields null (tripwire
  // stays disarmed rather than false-firing).
  let medCount = null;
  try {
    const ext = (comp.extension || []).find((e) => e && e.url === OPD_DRAFT && typeof e.valueString === 'string');
    if (ext) {
      const parsed = JSON.parse(ext.valueString);
      if (parsed && Array.isArray(parsed.medications)) medCount = parsed.medications.length;
    }
  } catch (_) { medCount = null; }
  return { id: comp.id, versionId: comp.meta && comp.meta.versionId, status: comp.status, medCount };
}

/** Establish the authenticated doctor's Patient-compartment stamp before any
 *  draft is saved or resolved for signing. Patient/$set-accounts propagation
 *  advances existing child-resource versions, so callers MUST run this before
 *  capturing the exact consult-draft version the doctor reviews and signs. */
export async function ensureSignAccessForEncounter(encounterId) {
  if (!encounterId) throw new Error('ensureSignAccessForEncounter: missing encounterId');
  const { medplum } = await getMedplum();
  const encounter = await medplum.readResource('Encounter', encounterId);
  const patientRef = String(encounter?.subject?.reference || '');
  const match = /^Patient\/([^/]+)$/.exec(patientRef);
  if (!match) throw new Error('ensureSignAccessForEncounter: Encounter has no Patient subject');
  const result = await grantChartAccess({
    patientId: match[1],
    reason: 'Signing assigned clinical encounter',
  });
  if (!result.alreadyGranted) {
    await new Promise((resolve) => setTimeout(resolve, ACCESS_INDEX_SETTLE_MS));
    if (typeof medplum.invalidateSearches === 'function') {
      try { medplum.invalidateSearches(); } catch { /* cache bust is best-effort; subsequent reads still go through Medplum */ }
    }
  }
  return result;
}

// Signs the prescription via the proven `sign` Bot (the POST /prescription/sign
// replacement). The browser sends only the reviewed consult-draft Composition id
// and version plus the explicit safety override. The Bot re-reads that exact FHIR
// version and Medplum supplies event.requester from the authenticated membership;
// neither clinical content nor signer identity is trusted from browser input.
// and returns the Bot's raw output, one of:
//   { alreadySigned:true, compositionId } | { blocked:true, alerts:[{drug,allergy}] }
//   | { signed:true, medCount, locations:[...] }
// The caller maps that onto the screen's existing success/failure transitions.
//   { encounterId, payload, draftRef } — payload carries only sign options
//   (overrideSafety); draftRef is the exact saved Composition id/version.
export async function signViaBot({ encounterId, payload, draftRef, expectedMedCount = null }) {
  if (!encounterId) throw new Error('signViaBot: missing encounterId');
  if (!draftRef?.id || !draftRef?.versionId) throw new Error('signViaBot: a saved draft id and version are required');
  const { medplum, cfg } = await getMedplum();
  if (!cfg || !cfg.bots || !cfg.bots.sign) throw new Error('signViaBot: no sign Bot configured');
  const access = await ensureSignAccessForEncounter(encounterId);
  if (!access.alreadyGranted) {
    throw new Error('signViaBot: chart access was established after draft review; reload and review the current saved draft before signing');
  }

  const input = {
    encounterId,
    draftCompositionId: draftRef.id,
    draftVersionId: draftRef.versionId,
    overrideSafety: !!(payload && payload.overrideSafety),
  };
  // executeBot wraps the handler return inconsistently ({returnValue} vs the value
  // directly) — defensively unwrap both, the pattern the spikes proved.
  const out = await medplum.executeBot(cfg.bots.sign, input);
  const result = (out && out.returnValue) || out;
  if (result && result.authorized === false) throw new Error(`Sign not authorized: ${result.reason || 'unknown reason'}`);
  // STALE-BOT TRIPWIRE (restored 2026-07-17, owner-ratified belt-and-braces).
  // The deploy-time gate in deploy-main2.mjs refuses to generate client config
  // unless the LIVE sign Bot's source SHA-256 matches this repo — but it only
  // guards SCRIPTED deploys; a manual rsync / OTA push bypasses it. This runtime
  // check is the second line: the sign-by-reference contract sends only the
  // draft id+version (never input.draft), so a stale Bot that still reads
  // input.draft signs an EMPTY prescription and returns medCount 0. When the
  // caller knows the saved draft held medications, a signed result claiming zero
  // can only be that mismatch — fail LOUD (§1.3) rather than let a med-less
  // prescription reach the patient. Nothing has been sent yet at the point the
  // Bot returns its result object.
  if (result && result.signed && Number.isFinite(expectedMedCount) && expectedMedCount > 0
      && typeof result.medCount === 'number' && result.medCount === 0) {
    throw new Error('sign bot mismatch: the signed result carries no medications although the saved draft has some — the sign-visit Bot deployment is older than this app; redeploy the Bot, then sign again');
  }
  return result;
}

// Renders a clinical document PDF via the `document-render` Bot (off the box disk).
// Replaces the Express pdfkit generators (prescription/lab-report/receipt/order-slip/
// visit-summary): the Bot writes a Binary (securityContext → Patient) + a
// DocumentReference (custodian = the encounter's department Organization, so the
// row-level AccessPolicy dept-scopes reads). runAsUser:true — the Bot renders and
// writes AS the logged-in staff member, RLS-bounded by their own token.
//
//   docType  — 'prescription' | 'lab-report' | 'receipt' | 'order-slip' | 'visit-summary'
//   sourceId — the source resource id (Encounter for all but the receipt, which
//              takes the Invoice id)
//   payload  — the structured content the old Express generators pulled from the
//              consult draft / discrete rows (draft, medications[], observations[],
//              amount/receiptNo/… ). Pass what the screen already holds in hand.
//   hospital — optional letterhead override (the SPA's loaded hospital identity);
//              the Bot falls back to the root hospital Organization when omitted.
//
// Returns the Bot output { ok, binaryId, documentReferenceId, attachmentUrl, … }.
// The resulting DocumentReference surfaces in the existing reports read (its
// attachment.url is auto-rewritten to a presigned URL on the doctor's authorized
// read) — no /uploads/ path, no box disk.
export async function renderDocumentViaBot({ docType, sourceId, payload, hospital, dedupeId }) {
  if (!docType) throw new Error('renderDocumentViaBot: missing docType');
  if (!sourceId) throw new Error('renderDocumentViaBot: missing sourceId');
  const { medplum, cfg } = await getMedplum();
  if (!cfg || !cfg.bots || !cfg.bots.documentRender) throw new Error('renderDocumentViaBot: no document-render Bot configured');
  const out = await medplum.executeBot(cfg.bots.documentRender, {
    docType, sourceId, payload: payload || {}, hospital, dedupeId,
  });
  return (out && out.returnValue) || out;
}

// ── Reception patient-edit (GROUP C: readResource + If-Match updateResource) ──
// A 412 on the demographics PUT means another desk saved this patient since the
// dialog loaded it. Same fail-loud contract as DraftConflictError above, but we
// also carry the CURRENT server copy so the dialog can render its field-by-field
// diff panel instead of silently overwriting the other writer (Reception.jsx R4).
export class PatientConflictError extends Error {
  constructor(current) {
    super('patient changed on the server since it was loaded');
    this.name = 'PatientConflictError';
    this.conflict = true;
    this.status = 412;
    this.current = current || null; // the freshly-read FHIR Patient
  }
}

// Front-desk demographics correction, FHIR-direct as the logged-in receptionist
// (RLS-scoped by their own token). `resource` is the FHIR Patient the dialog
// loaded (medplum.readResource), `versionId` its meta.versionId at load time —
// the optimistic lock. `fields` is the dialog's edit shape
// { name, phone, age, sex, language }; phone:'' / age:null are legitimate
// "clear this field" writes (the old Express patient-edit contract), MRN and
// every untouched FHIR field ride through unchanged.
export async function updatePatientInMedplum({ resource, versionId, fields }) {
  if (!resource || resource.resourceType !== 'Patient' || !resource.id) {
    throw new Error('updatePatientInMedplum: missing the loaded FHIR Patient resource');
  }
  const { medplum } = await getMedplum();
  const f = fields || {};
  const next = { ...resource };

  // name → name[0].text (readers prefer .text — see shape/queue-row.js nameOf).
  // given/family are dropped from that entry when the text is edited, so a stale
  // structured name can never disagree with the corrected display name.
  if (f.name !== undefined && String(f.name).trim()) {
    const n0 = (Array.isArray(next.name) && next.name[0]) || {};
    const { given: _g, family: _f, ...rest } = n0;
    next.name = [{ ...rest, text: String(f.name).trim() }, ...(Array.isArray(next.name) ? next.name.slice(1) : [])];
  }
  // phone → the telecom phone entry; empty string clears it.
  if (f.phone !== undefined) {
    const others = (next.telecom || []).filter((t) => !(t && t.system === 'phone'));
    next.telecom = f.phone ? [{ system: 'phone', value: String(f.phone) }, ...others] : others;
    if (!next.telecom.length) delete next.telecom;
  }
  // sex F/M → gender; '' clears it.
  if (f.sex !== undefined) {
    if (f.sex === 'F') next.gender = 'female';
    else if (f.sex === 'M') next.gender = 'male';
    else delete next.gender;
  }
  // age → birthDate year (the disk model stored a bare age; FHIR stores
  // birthDate — year precision is what every reader derives age from, see
  // ageOf in shape/queue-row.js). Preserve an existing month-day; null clears.
  if (f.age !== undefined) {
    if (f.age === null || f.age === '') {
      delete next.birthDate;
    } else {
      const age = Number(f.age);
      if (!Number.isFinite(age) || age < 0 || age > 130) throw new Error('bad age');
      const year = new Date().getUTCFullYear() - Math.round(age);
      const monthDay = /^\d{4}(-\d{2}-\d{2})/.exec(String(resource.birthDate || ''));
      next.birthDate = String(year) + (monthDay ? monthDay[1] : '-01-01');
    }
  }
  // language → communication[0].language (FHIR-correct home for it).
  if (f.language !== undefined && f.language) {
    const lang = String(f.language);
    next.communication = [
      { language: { coding: [{ system: 'urn:ietf:bcp:47', code: lang }], text: lang }, preferred: true },
      ...((next.communication || []).slice(1)),
    ];
  }

  // Through the offline outbox (op:'update'). Online, enqueue() drains
  // synchronously → a 412 re-throws PatientConflictError carrying the winner's
  // copy for the dialog's diff panel (unchanged UX). Offline, the edit queues
  // and replays on reconnect (a 412 on that path parks the row 'conflict').
  // Async profile capture + fail-loud on absence (O5) — same wrong-user-guard
  // rationale as saveDraftToMedplum: profileId must always be present so the
  // engine's different-profile guard (outbox.js:167) fires on replay.
  const profile = await medplum.getProfileAsync();
  if (!profile) throw new Error('updatePatientInMedplum: no logged-in profile — refusing to queue a clinical write with no known author (O5)');
  const landed = await enqueueUpdate(
    { resourceType: 'Patient', body: next, targetId: resource.id, ifMatch: versionId, profileId: profile.id },
    (current) => new PatientConflictError(current),
    () => medplum.readResource('Patient', resource.id),
  );
  // FAIL LOUD on a REJECTED write (M-7): a non-retryable 4xx (403 AccessPolicy /
  // 422) parks the row 'failed'. Returning the optimistic copy here reported a
  // rejected demographics correction as SAVED — the sheet closed "saved" and the
  // edit silently reverted on the next load. Throw the parked error so
  // PatientEditSheet's catch surfaces it. (A queued offline row is NOT failed —
  // it returns the optimistic copy below, the justified outbox contract.)
  if (landed && landed.status === 'failed') {
    throw new Error(landed.lastError || 'patient update was rejected by Medplum');
  }
  if (landed && landed.status === 'done') {
    // Online success: return the optimistic copy stamped with the FRESH server
    // versionId (captured at drain time) so the sheet's NEXT save carries the
    // current version and doesn't 412 (m-29). Re-read only if the drain didn't
    // carry a version.
    if (landed.resolvedVersionId) {
      return { ...next, id: landed.resolvedId || next.id, meta: { ...(next.meta || {}), versionId: landed.resolvedVersionId } };
    }
    return medplum.readResource('Patient', landed.resolvedId || resource.id);
  }
  // Offline (queued): the optimistic next resource is what the caller sees; the
  // real write replays on reconnect.
  return next;
}

// ── Priority flag (GROUP C: Encounter.priority + queue-class stat) ────────────
// The doctor/reception "mark this waiting patient urgent" action. Replaces the
// deleted Express POST /encounters/:id/flag with a FHIR-direct read-modify-write
// as the logged-in caller (reception: unscoped Encounter write; doctor: own-dept
// RLS Encounter write). Fail loud: bridge-core's click handler surfaces the error.
//
// TWO writes in ONE update, because two different consumers read two different
// fields (they must never disagree):
//   1. Encounter.priority = v3-ActPriority 'UR' (urgent) — the FHIR-correct home
//      for clinical urgency (surfaced to any FHIR reader / report).
//   2. The opd/queue-class extension → 'stat' — the LIVE QUEUE ENGINE's ordering
//      key (queue-engine.js queueOrder ranks stat first). Setting priority WITHOUT
//      this left the engine ordering unchanged (the historical gap): the flag read
//      as urgent everywhere except the one place it must act — the queue position.
//      We PRESERVE effectiveSince (the original check-in instant) so an urgent
//      patient jumps to the FRONT of their class but keeps their arrival time as
//      the within-class tiebreak — they don't leapfrog an earlier-arriving stat.
//
// Optimistic-concurrency guarded (If-Match on the read version) so a concurrent
// call/edit can't be silently clobbered — same contract as medplum-calling.js.
export async function setEncounterPriorityInMedplum(encounterId) {
  if (!encounterId) throw new Error('setEncounterPriorityInMedplum: missing encounterId');
  const { medplum } = await getMedplum();
  const enc = await medplum.readResource('Encounter', encounterId);

  // Queue-class → stat, keeping the original effectiveSince (arrival instant).
  const { effectiveSince } = readQueueClass(enc);
  const classExt = buildQueueClassExtension({ queueClass: QUEUE_CLASS.STAT, effectiveSince });
  const otherExts = ((enc && enc.extension) || []).filter((e) => e && e.url !== OPD_QUEUE_CLASS_EXT);

  const next = {
    ...enc,
    priority: {
      coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority', code: 'UR', display: 'urgent' }],
      text: 'urgent',
    },
    extension: [...otherExts, classExt],
  };
  const versionId = enc.meta && enc.meta.versionId;
  const opts = versionId ? { headers: { 'If-Match': 'W/"' + versionId + '"' } } : undefined;
  const saved = await medplum.updateResource(next, opts);
  try { medplum.invalidateSearches('Encounter'); } catch (_) { /* best-effort cache nudge */ }
  return saved;
}
